import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { BellRing } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { api } from "../../lib/api";
import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../ui/dialog";

type CoachingNotice = {
  id: string;
  kind: "coaching_assigned";
  title: string;
  body: string;
  href: string;
  due_at?: string;
};

export function CoachingAssignmentAlert() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [notice, setNotice] = useState<CoachingNotice | null>(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [dismissed, setDismissed] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!user || user.scope === "platform") return;
    let active = true;
    api.get("/notifications", { params: { status: "unread", limit: 200 } })
      .then(({ data }) => {
        if (!active) return;
        const coaching = (data.items || [])
          .filter((item: CoachingNotice) => item.kind === "coaching_assigned")
          .sort((a: CoachingNotice, b: CoachingNotice) =>
            String(b.due_at || "").localeCompare(String(a.due_at || "")),
          )[0];
        setUnreadCount(Number(data.unread_count) || 0);
        setNotice(coaching || null);
      })
      .catch(() => undefined);
    return () => { active = false; };
  }, [user]);

  const viewNow = async () => {
    if (!notice) return;
    setBusy(true);
    try {
      await api.post("/notifications/read", { notification_ids: [notice.id] });
      window.dispatchEvent(new CustomEvent("remedikeep:notification-count", {
        detail: Math.max(0, unreadCount - 1),
      }));
      setNotice(null);
      navigate(notice.href);
    } finally {
      setBusy(false);
    }
  };

  if (!notice || dismissed) return null;

  return (
    <Dialog open onOpenChange={(open) => { if (!open && !busy) setDismissed(true); }}>
      <DialogContent data-testid="coaching-assignment-alert">
        <DialogHeader>
          <div className="mb-2 grid h-11 w-11 place-items-center rounded-full bg-blue-50 text-blue-700">
            <BellRing className="h-5 w-5" />
          </div>
          <DialogTitle>{notice.title}</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-slate-600">{notice.body}</p>
        <p className="rounded-md bg-amber-50 p-3 text-xs text-amber-900">
          Viewing the record does not acknowledge it. Your formal acknowledgment and signature are completed inside Coaching.
        </p>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => setDismissed(true)} disabled={busy}>
            View later
          </Button>
          <Button type="button" onClick={() => void viewNow()} disabled={busy} data-testid="view-coaching-now">
            {busy ? "Opening…" : "Click to view"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
